import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ChevronRightIcon,
  RocketLaunchIcon,
  ArrowPathIcon,
  CubeIcon,
  ServerIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CursorArrowRaysIcon,
  CloudIcon,
  ShieldCheckIcon,
  SignalIcon,
  ClockIcon,
  BellIcon,
  SparklesIcon,
  PlayIcon,
  LockClosedIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { useOnboardingStatus, OnboardingStep } from './useOnboardingStatus';

interface OnboardingHubProps {
  variant?: 'full' | 'compact' | 'banner';
  showDismiss?: boolean;
  onDismiss?: () => void;
  onStartTour?: () => void;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'cube': CubeIcon,
  'server': ServerIcon,
  'chart-bar': ChartBarIcon,
  'document-text': DocumentTextIcon,
  'cursor-arrow-rays': CursorArrowRaysIcon,
  'cloud': CloudIcon,
  'shield-check': ShieldCheckIcon,
  'signal': SignalIcon,
  'clock': ClockIcon,
  'bell': BellIcon,
  'sparkles': SparklesIcon,
};

const categoryConfig: Record<string, {
  gradient: string;
  bg: string;
  bgHover: string;
  text: string;
  border: string;
  dot: string;
  emoji: string;
}> = {
  foundation: {
    gradient: 'from-blue-500 to-cyan-500',
    bg: 'bg-blue-500/10',
    bgHover: 'hover:bg-blue-500/20',
    text: 'text-blue-500',
    border: 'border-blue-500/30',
    dot: 'bg-blue-500',
    emoji: '🏗️',
  },
  agents: {
    gradient: 'from-purple-500 to-pink-500',
    bg: 'bg-purple-500/10',
    bgHover: 'hover:bg-purple-500/20',
    text: 'text-purple-500',
    border: 'border-purple-500/30',
    dot: 'bg-purple-500',
    emoji: '🔌',
  },
  scanners: {
    gradient: 'from-emerald-500 to-teal-500',
    bg: 'bg-emerald-500/10',
    bgHover: 'hover:bg-emerald-500/20',
    text: 'text-emerald-500',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-500',
    emoji: '🔍',
  },
  monitoring: {
    gradient: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-500/10',
    bgHover: 'hover:bg-amber-500/20',
    text: 'text-amber-500',
    border: 'border-amber-500/30',
    dot: 'bg-amber-500',
    emoji: '📊',
  },
  advanced: {
    gradient: 'from-rose-500 to-red-500',
    bg: 'bg-rose-500/10',
    bgHover: 'hover:bg-rose-500/20',
    text: 'text-rose-500',
    border: 'border-rose-500/30',
    dot: 'bg-rose-500',
    emoji: '🚀',
  },
};

const categoryOrder = ['foundation', 'agents', 'scanners', 'monitoring', 'advanced'];

export default function OnboardingHub({
  variant = 'full',
  showDismiss = true,
  onDismiss,
  onStartTour,
}: OnboardingHubProps) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const {
    steps,
    completedCount,
    totalCount,
    percentage,
    isComplete,
    loading,
    refresh,
  } = useOnboardingStatus();

  const [dismissed, setDismissed] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    foundation: true,
    agents: true,
    scanners: true,
    monitoring: true,
    advanced: true,
  });

  // Group steps by category
  const stepsByCategory = useMemo(() => {
    const grouped: Record<string, OnboardingStep[]> = {};
    steps.forEach(step => {
      if (!grouped[step.category]) grouped[step.category] = [];
      grouped[step.category].push(step);
    });
    return grouped;
  }, [steps]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <ArrowPathIcon className="w-6 h-6 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (isComplete || dismissed) {
    return null;
  }

  // Check if step dependencies are satisfied
  const isDependencySatisfied = (step: OnboardingStep) => {
    if (!step.dependencies) return true;
    return step.dependencies.every(depId => steps.find(s => s.id === depId)?.completed);
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Banner variant - minimal banner
  if (variant === 'banner') {
    const pendingCount = steps.filter(s => !s.completed).length;

    return (
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl sm:rounded-2xl p-0.5 sm:p-1">
        <div className="bg-white dark:bg-gray-900 rounded-lg sm:rounded-xl p-3 sm:p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <RocketLaunchIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-amber-500 text-white text-[10px] sm:text-xs font-bold flex items-center justify-center">
                  {pendingCount}
                </div>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">{t('banner.title')}</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  {completedCount}/{totalCount} {t('progress.completed')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <div className="hidden sm:block w-24 lg:w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>

              {/* Tour button - hidden on mobile */}
              {onStartTour && (
                <button
                  onClick={onStartTour}
                  className="hidden lg:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all hover:scale-105"
                >
                  <PlayIcon className="w-4 h-4" />
                  {t('actions.startTour')}
                </button>
              )}

              {showDismiss && (
                <button
                  onClick={() => setDismissed(true)}
                  className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compact variant
  if (variant === 'compact') {
    const pendingSteps = steps.filter(s => !s.completed);

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white/20 flex items-center justify-center">
                <RocketLaunchIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-bold text-white">{t('title.setup')}</h3>
                <p className="text-xs sm:text-sm text-white/70">{completedCount}/{totalCount} {t('progress.completed')}</p>
              </div>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">{percentage}%</div>
          </div>
        </div>

        {/* Quick list */}
        <div className="p-3 sm:p-4 space-y-2">
          {pendingSteps.slice(0, 4).map(step => {
            const Icon = iconMap[step.icon] || CubeIcon;
            const config = categoryConfig[step.category];
            const isLocked = !isDependencySatisfied(step);

            return (
              <button
                key={step.id}
                onClick={() => !isLocked && navigate(step.href)}
                disabled={isLocked}
                className={`
                  w-full flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all
                  ${isLocked
                    ? 'bg-gray-100 dark:bg-gray-700/50 opacity-50 cursor-not-allowed'
                    : `${config.bg} ${config.bgHover} cursor-pointer`
                  }
                `}
              >
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center ${isLocked ? 'bg-gray-300 dark:bg-gray-600' : config.dot}`}>
                  {isLocked ? (
                    <LockClosedIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  ) : (
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  )}
                </div>
                <span className={`flex-1 text-left text-xs sm:text-sm font-medium ${isLocked ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                  {t(step.name)}
                </span>
                {!isLocked && <ChevronRightIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />}
              </button>
            );
          })}

          {pendingSteps.length > 4 && (
            <p className="w-full text-center text-xs sm:text-sm text-indigo-600 dark:text-indigo-400 font-medium py-2">
              +{pendingSteps.length - 4} {t('progress.more')}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Full variant - Main dashboard component
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
      {/* Header - Mobile */}
      <div className="sm:hidden relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600" />
        <div className="relative p-4">
          {/* Top row: Icon + Title + Percentage */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <RocketLaunchIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">{t('title.setup')}</h2>
                <p className="text-xs text-white/70">{completedCount}/{totalCount} {t('progress.completed')}</p>
              </div>
            </div>
            <div className="text-2xl font-bold text-white">{percentage}%</div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${percentage}%` }}
            />
          </div>

          {/* Quick stats - horizontal scroll on mobile */}
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
            {categoryOrder.map(category => {
              const categorySteps = stepsByCategory[category] || [];
              if (categorySteps.length === 0) return null;

              const config = categoryConfig[category];
              const completedInCategory = categorySteps.filter(s => s.completed).length;
              const isAllComplete = completedInCategory === categorySteps.length;

              return (
                <div
                  key={category}
                  className={`
                    flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-xs
                    ${isAllComplete ? 'bg-green-500/30' : 'bg-white/10'}
                  `}
                >
                  <span>{config.emoji}</span>
                  <span className="font-medium text-white">{completedInCategory}/{categorySteps.length}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Header - Desktop */}
      <div className="hidden sm:block relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600" />
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />

        <div className="relative p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                <RocketLaunchIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{t('title.setup')}</h2>
                <p className="text-white/70">{t('description.welcome')}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Progress ring */}
              <div className="relative">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
                  <circle
                    cx="32" cy="32" r="28" fill="none" stroke="white" strokeWidth="4"
                    strokeDasharray={`${percentage * 1.76} 176`}
                    strokeLinecap="round"
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-white">{percentage}%</span>
                </div>
              </div>

              {onStartTour && (
                <button
                  onClick={onStartTour}
                  className="hidden lg:flex items-center gap-2 px-5 py-2.5 bg-white text-indigo-600 font-bold rounded-xl hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl hover:scale-105"
                >
                  <PlayIcon className="w-5 h-5" />
                  {t('actions.startTour')}
                </button>
              )}
            </div>
          </div>

          {/* Quick stats */}
          <div className="mt-6 flex flex-wrap gap-3">
            {categoryOrder.map(category => {
              const categorySteps = stepsByCategory[category] || [];
              if (categorySteps.length === 0) return null;

              const config = categoryConfig[category];
              const completedInCategory = categorySteps.filter(s => s.completed).length;
              const isAllComplete = completedInCategory === categorySteps.length;

              return (
                <div
                  key={category}
                  className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-lg
                    ${isAllComplete ? 'bg-green-500/20' : 'bg-white/10'}
                    backdrop-blur-sm
                  `}
                >
                  <span className="text-lg">{config.emoji}</span>
                  <span className="text-sm font-medium text-white">
                    {completedInCategory}/{categorySteps.length}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Steps by category */}
      <div className="p-2 sm:p-4 space-y-3 sm:space-y-4">
        {categoryOrder.map(category => {
          const categorySteps = stepsByCategory[category] || [];
          if (categorySteps.length === 0) return null;

          const config = categoryConfig[category];
          const completedInCategory = categorySteps.filter(s => s.completed).length;
          const isExpanded = expandedCategories[category];
          const hasPending = completedInCategory < categorySteps.length;

          return (
            <div key={category} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              {/* Category header */}
              <button
                onClick={() => toggleCategory(category)}
                className={`
                  w-full flex items-center justify-between p-3 sm:p-4 transition-colors
                  ${hasPending ? `${config.bg} ${config.bgHover}` : 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'}
                `}
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <span className="text-xl sm:text-2xl flex-shrink-0">{config.emoji}</span>
                  <div className="text-left min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">
                      {t(`categories.${category}`)}
                    </h3>
                    <p className="hidden sm:block text-sm text-gray-500 dark:text-gray-400">
                      {t(`categoryDescriptions.${category}`)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                  <div className={`
                    px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-semibold
                    ${completedInCategory === categorySteps.length
                      ? 'bg-green-500 text-white'
                      : `${config.bg} ${config.text}`
                    }
                  `}>
                    {completedInCategory}/{categorySteps.length}
                  </div>
                  <ChevronRightIcon className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </div>
              </button>

              {/* Steps */}
              {isExpanded && (
                <div className="p-2 sm:p-3 bg-gray-50 dark:bg-gray-900/50 space-y-2">
                  {categorySteps.map((step) => {
                    const Icon = iconMap[step.icon] || CubeIcon;
                    const isLocked = !isDependencySatisfied(step) && !step.completed;

                    return (
                      <div
                        key={step.id}
                        onClick={() => !isLocked && !step.completed && navigate(step.href)}
                        className={`
                          group flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 transition-all
                          ${step.completed
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                            : isLocked
                              ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-60'
                              : `bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-${config.dot.replace('bg-', '')} hover:shadow-lg cursor-pointer`
                          }
                        `}
                      >
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                          {/* Icon */}
                          <div className={`
                            flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center
                            ${step.completed
                              ? 'bg-green-500'
                              : isLocked
                                ? 'bg-gray-300 dark:bg-gray-600'
                                : `bg-gradient-to-br ${config.gradient}`
                            }
                            shadow-lg
                          `}>
                            {step.completed ? (
                              <CheckCircleSolidIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            ) : isLocked ? (
                              <LockClosedIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            ) : (
                              <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <h4 className={`text-sm sm:text-base font-semibold ${
                              step.completed
                                ? 'text-green-700 dark:text-green-400'
                                : isLocked
                                  ? 'text-gray-400 dark:text-gray-500'
                                  : 'text-gray-900 dark:text-white'
                            }`}>
                              {t(step.name)}
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                              {t(step.description)}
                            </p>
                          </div>

                          {/* Completed badge - mobile */}
                          {step.completed && (
                            <span className="sm:hidden flex-shrink-0 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-lg">
                              ✓
                            </span>
                          )}
                        </div>

                        {/* Action - full width on mobile */}
                        <div className="w-full sm:w-auto flex justify-end sm:block">
                          {step.completed ? (
                            <span className="hidden sm:inline-flex flex-shrink-0 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-medium rounded-lg">
                              {t('status.completed')}
                            </span>
                          ) : isLocked ? (
                            <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                              {t('status.completePrevious')}
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(step.href);
                              }}
                              className={`
                                flex-shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg
                                bg-gradient-to-r ${config.gradient}
                                text-white font-semibold text-xs sm:text-sm
                                shadow-md hover:shadow-lg
                                sm:opacity-0 sm:group-hover:opacity-100
                                transition-all hover:scale-105
                              `}
                            >
                              {t('actions.configure')}
                              <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <div className="flex items-center justify-between">
          <button
            onClick={refresh}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowPathIcon className="w-4 h-4" />
            <span className="hidden sm:inline">{t('actions.refresh')}</span>
          </button>

          {showDismiss && (
            <button
              onClick={() => setDismissed(true)}
              className="text-xs sm:text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {t('actions.skip')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
